import { useState, useEffect } from 'react'

export type ScrollDirection = 'up' | 'down'

/**
 * Tracks scroll direction, returning 'up' or 'down'.
 * Only changes direction after a minimum scroll threshold to avoid jitter.
 * When minScrollY is set, always reports 'up' while scrollY is within that zone
 * (prevents iOS overscroll bounce from triggering hide).
 * Also returns isAtTop: true when scrollY is within the minScrollY dead zone.
 */
export function useScrollDirection(
    threshold = 10,
    minScrollY = 0
): { scrollDirection: ScrollDirection; isAtTop: boolean } {
    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('up')
    const [isAtTop, setIsAtTop] = useState(() => window.scrollY <= minScrollY)

    useEffect(() => {
        let lastScrollY = window.scrollY

        const onScroll = () => {
            const scrollY = window.scrollY

            setIsAtTop(scrollY <= minScrollY)

            if (scrollY <= minScrollY) {
                setScrollDirection('up')
                lastScrollY = scrollY
                return
            }

            const diff = scrollY - lastScrollY

            if (Math.abs(diff) >= threshold) {
                setScrollDirection(diff > 0 ? 'down' : 'up')
                lastScrollY = scrollY
            }
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [threshold, minScrollY])

    return { scrollDirection, isAtTop }
}
